/**
 * B2B Matrix — bulk order grid with optional per-unit "Add Name" personalisation.
 *
 * Reads its config from the #B2BMatrixData-<sectionId> JSON blob rendered by
 * sections/b2b-matrix.liquid (variant tuples, wholesale prices, personalisation
 * fields, fee settings).
 *
 * Cart mechanic for personalisation: Shopify merges line items with identical
 * variant + properties, so each NAMED unit is added as its own quantity-1 line
 * carrying its field values plus a unique hidden `_pid`; the unnamed remainder
 * of that variant is collapsed into a single plain line. The fee is a hidden
 * fee product added with quantity = named-unit count (or per-line / per-order,
 * per the section's fee basis setting).
 */
(function () {
  'use strict';

  var SOLD_OUT_MSG = 'Some items are no longer available. Please refresh the page.';

  function formatMoney(cents, moneyFormat) {
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(cents, moneyFormat || theme.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  var pidCounter = 0;
  function uniquePid() {
    pidCounter += 1;
    if (window.crypto && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID().slice(0, 13) + '-' + pidCounter;
    }
    return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8) + '-' + pidCounter;
  }

  function initSection(container) {
    if (container.dataset.b2bMatrixInit === 'true') return;
    container.dataset.b2bMatrixInit = 'true';

    var sectionId = container.getAttribute('data-section-id');
    var dataEl = document.getElementById('B2BMatrixData-' + sectionId);
    if (!dataEl) return;

    var config;
    try {
      config = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('B2B Matrix: invalid config JSON', e);
      return;
    }

    // Trailing null is a Liquid loop artefact — drop it.
    config.personalisationFields = (config.personalisationFields || []).filter(Boolean);
    if (config.enableAddName && config.personalisationFields.length === 0) {
      config.personalisationFields = [
        { label: 'Name', key: 'Name', required: true, placeholder: 'Enter name' }
      ];
    }

    var feeVariant = null;
    var feeEl = document.getElementById('B2BFeeVariant-' + sectionId);
    if (feeEl) {
      try { feeVariant = JSON.parse(feeEl.textContent); } catch (e) { /* noop */ }
    }
    if (config.enableAddName && !feeVariant) {
      console.warn('B2B Matrix: fee product "' + config.feeProductHandle + '" not found or unavailable. Personalisation will be captured but no fee can be charged.');
    }
    if (config.enableAddName && feeVariant && feeVariant.price !== config.addNameFee) {
      console.warn('B2B Matrix: section fee setting (' + config.addNameFee + 'c) differs from fee product price (' + feeVariant.price + 'c). The fee product price is what will be charged.');
    }
    // The fee product's real price is what the customer pays — trust it for display.
    var feeUnitPrice = feeVariant ? feeVariant.price : config.addNameFee;

    var variantMap = {};
    config.variants.forEach(function (v) { variantMap[v.id] = v; });

    // ---- Elements ----
    var qtyInputs = Array.prototype.slice.call(container.querySelectorAll('.b2b-matrix__qty'));
    var addBtn = container.querySelector('[data-add-button]');
    var statusEl = container.querySelector('[data-status]');
    var itemCountEl = container.querySelector('[data-item-count]');
    var btnCountEl = container.querySelector('[data-btn-count]');
    var subtotalEl = container.querySelector('[data-subtotal]');
    var feeTotalEl = container.querySelector('[data-fee-total]');
    var feeAmountEl = container.querySelector('[data-fee-amount]');
    var namedCountEl = container.querySelector('[data-named-count]');
    var addNamePanel = container.querySelector('[data-add-name-panel]');
    var nameLinesEl = container.querySelector('[data-name-lines]');

    if (!addBtn || !statusEl) return;

    /**
     * Personalisation state, keyed by variantId:
     *   { enabled: bool, pasteOpen: bool, units: [ { <fieldKey>: value, ... } ] }
     * `units.length` always tracks the line's current quantity.
     */
    var persState = {};

    // ---- Matrix state ----

    function getFilledCells() {
      var cells = [];
      qtyInputs.forEach(function (input) {
        var qty = parseInt(input.value, 10);
        if (!isNaN(qty) && qty > 0) {
          cells.push({
            variantId: parseInt(input.getAttribute('data-variant-id'), 10),
            quantity: qty,
            label: input.getAttribute('data-cell-label') || ''
          });
        }
      });
      return cells;
    }

    function getTotalQuantity(cells) {
      return cells.reduce(function (sum, c) { return sum + c.quantity; }, 0);
    }

    function getSubtotal(cells) {
      return cells.reduce(function (sum, c) {
        var v = variantMap[c.variantId];
        return sum + (v ? v.price * c.quantity : 0);
      }, 0);
    }

    function emptyUnit() {
      var unit = {};
      config.personalisationFields.forEach(function (f) { unit[f.key] = ''; });
      return unit;
    }

    /** Keep persState lines in sync with the grid: prune gone lines, resize unit arrays. */
    function syncPersState(cells) {
      var activeIds = {};
      cells.forEach(function (cell) {
        activeIds[cell.variantId] = true;
        var st = persState[cell.variantId];
        if (!st) {
          st = persState[cell.variantId] = { enabled: false, pasteOpen: false, units: [] };
        }
        while (st.units.length < cell.quantity) st.units.push(emptyUnit());
        if (st.units.length > cell.quantity) st.units.length = cell.quantity;
      });
      Object.keys(persState).forEach(function (id) {
        if (!activeIds[id]) delete persState[id];
      });
    }

    /** A unit counts as personalised when every required field is filled (or, with no required fields, when any field is filled). */
    function isNamedUnit(unit) {
      var requiredFields = config.personalisationFields.filter(function (f) { return f.required; });
      if (requiredFields.length > 0) {
        return requiredFields.every(function (f) { return (unit[f.key] || '').trim() !== ''; });
      }
      return config.personalisationFields.some(function (f) { return (unit[f.key] || '').trim() !== ''; });
    }

    /** A unit with optional fields filled but a required field blank — needs fixing before submit. */
    function isPartialUnit(unit) {
      if (isNamedUnit(unit)) return false;
      return config.personalisationFields.some(function (f) { return (unit[f.key] || '').trim() !== ''; });
    }

    function countNamedUnits(cells) {
      var total = 0, lines = 0;
      cells.forEach(function (cell) {
        var st = persState[cell.variantId];
        if (!st || !st.enabled) return;
        var named = st.units.filter(isNamedUnit).length;
        total += named;
        if (named > 0) lines += 1;
      });
      return { total: total, lines: lines };
    }

    function feeQuantity(named) {
      if (named.total === 0) return 0;
      if (config.feeBasis === 'per_line') return named.lines;
      if (config.feeBasis === 'per_order') return 1;
      return named.total; // per_unit (default)
    }

    // ---- Personalisation UI ----

    function renderPersonalisation() {
      if (!config.enableAddName || !addNamePanel || !nameLinesEl) return;

      var cells = getFilledCells();
      syncPersState(cells);

      if (cells.length === 0) {
        addNamePanel.style.display = 'none';
        nameLinesEl.innerHTML = '';
        return;
      }
      addNamePanel.style.display = 'block';

      var html = '';
      cells.forEach(function (cell) {
        var st = persState[cell.variantId];
        var label = escapeHtml(cell.label || (variantMap[cell.variantId] || {}).title || 'Item');

        html += '<div class="b2b-matrix__name-line" data-line-variant-id="' + cell.variantId + '">'
          + '<div class="b2b-matrix__name-line-head">'
          + '<span class="b2b-matrix__name-line-title">' + label + '</span>'
          + '<span class="b2b-matrix__name-line-qty">&times;' + cell.quantity + '</span>'
          + '<label class="b2b-matrix__name-line-toggle">'
          + '<input type="checkbox" data-line-toggle data-variant-id="' + cell.variantId + '"' + (st.enabled ? ' checked' : '') + '>'
          + '<span>' + escapeHtml(config.addNameLabel) + '</span>'
          + '</label>'
          + '</div>';

        if (st.enabled) {
          html += '<div class="b2b-matrix__name-units">';

          if (cell.quantity > 1) {
            html += '<div class="b2b-matrix__name-tools">'
              + '<button type="button" class="b2b-matrix__tool-btn" data-apply-all data-variant-id="' + cell.variantId + '">Apply first item to all</button>'
              + '<button type="button" class="b2b-matrix__tool-btn" data-paste-toggle data-variant-id="' + cell.variantId + '">' + (st.pasteOpen ? 'Hide paste list' : 'Paste a list of names') + '</button>'
              + '</div>';
            if (st.pasteOpen) {
              html += '<div class="b2b-matrix__paste-wrap">'
                + '<textarea class="b2b-matrix__paste-area" data-paste-area data-variant-id="' + cell.variantId + '" placeholder="One name per line&#10;JAMIE&#10;ALEX&#10;SAM"></textarea>'
                + '<p class="b2b-matrix__paste-hint">One name per line, applied to items in order. Extra lines are ignored.</p>'
                + '<button type="button" class="b2b-matrix__tool-btn" data-paste-apply data-variant-id="' + cell.variantId + '">Apply list</button>'
                + '</div>';
            }
          }

          st.units.forEach(function (unit, unitIndex) {
            html += '<div class="b2b-matrix__unit-row" data-variant-id="' + cell.variantId + '" data-unit-index="' + unitIndex + '">'
              + '<span class="b2b-matrix__unit-label">Item ' + (unitIndex + 1) + '</span>';
            config.personalisationFields.forEach(function (field) {
              html += '<input type="text" class="b2b-matrix__name-input"'
                + ' data-name-input'
                + ' data-variant-id="' + cell.variantId + '"'
                + ' data-unit-index="' + unitIndex + '"'
                + ' data-field-key="' + escapeHtml(field.key) + '"'
                + ' placeholder="' + escapeHtml(field.placeholder || field.label) + '"'
                + ' value="' + escapeHtml(unit[field.key] || '') + '"'
                + ' aria-label="' + label + ' item ' + (unitIndex + 1) + ' ' + escapeHtml(field.label) + '"'
                + ' autocomplete="off">';
            });
            html += '</div>';
          });

          html += '</div>';
        }

        html += '</div>';
      });

      nameLinesEl.innerHTML = html;
    }

    // ---- Summary / totals ----

    function updateSummary(skipPersRender) {
      var cells = getFilledCells();
      syncPersState(cells);
      var total = getTotalQuantity(cells);
      var subtotal = getSubtotal(cells);

      if (itemCountEl) itemCountEl.textContent = total;
      if (btnCountEl) btnCountEl.textContent = total;
      if (subtotalEl) subtotalEl.innerHTML = formatMoney(subtotal, config.moneyFormat);
      addBtn.disabled = total === 0;

      if (config.enableAddName && feeTotalEl && feeAmountEl) {
        var named = countNamedUnits(cells);
        var feeQty = feeQuantity(named);
        if (feeQty > 0) {
          feeAmountEl.innerHTML = formatMoney(feeQty * feeUnitPrice, config.moneyFormat);
          if (namedCountEl) namedCountEl.textContent = named.total;
          feeTotalEl.style.display = 'block';
        } else {
          feeTotalEl.style.display = 'none';
        }
      }

      qtyInputs.forEach(function (input) {
        input.classList.toggle('has-value', parseInt(input.value, 10) > 0);
      });

      if (!skipPersRender) renderPersonalisation();

      setStatus('', '');
    }

    function setStatus(msg, kind) {
      statusEl.textContent = msg;
      statusEl.className = 'b2b-matrix__status' + (kind ? ' b2b-matrix__status--' + kind : '');
    }

    // ---- Quantity input handling ----

    qtyInputs.forEach(function (input) {
      input.addEventListener('input', function () {
        var val = this.value.replace(/[^0-9]/g, '');
        // Clamp to available stock when inventory is tracked and oversell is denied.
        var v = variantMap[parseInt(this.getAttribute('data-variant-id'), 10)];
        if (val !== '' && v && v.inventoryManaged && v.inventoryPolicy === 'deny' && v.inventoryQuantity > 0 && parseInt(val, 10) > v.inventoryQuantity) {
          this.value = String(v.inventoryQuantity);
          updateSummary(); // clears status, so set the stock message after
          setStatus('Only ' + v.inventoryQuantity + ' in stock for ' + (this.getAttribute('data-cell-label') || v.title) + '.', 'error');
          return;
        }
        this.value = val;
        updateSummary();
      });
      input.addEventListener('change', function () {
        var val = parseInt(this.value, 10);
        if (isNaN(val) || val < 0) this.value = '';
        updateSummary();
      });
    });

    // ---- Personalisation events (delegated; typing never re-renders) ----

    if (nameLinesEl) {
      nameLinesEl.addEventListener('input', function (e) {
        var t = e.target;
        if (t.matches('[data-name-input]')) {
          var st = persState[t.getAttribute('data-variant-id')];
          var idx = parseInt(t.getAttribute('data-unit-index'), 10);
          if (st && st.units[idx]) {
            st.units[idx][t.getAttribute('data-field-key')] = t.value;
            t.classList.remove('error');
            updateSummary(true); // totals only — keep focus in this input
          }
        }
      });

      nameLinesEl.addEventListener('change', function (e) {
        if (e.target.matches('[data-line-toggle]')) {
          var st = persState[e.target.getAttribute('data-variant-id')];
          if (st) {
            st.enabled = e.target.checked;
            updateSummary();
          }
        }
      });

      nameLinesEl.addEventListener('click', function (e) {
        var btn = e.target.closest('button');
        if (!btn) return;
        var variantId = btn.getAttribute('data-variant-id');
        var st = persState[variantId];
        if (!st) return;

        if (btn.hasAttribute('data-apply-all')) {
          var first = st.units[0];
          if (first) {
            for (var i = 1; i < st.units.length; i++) {
              config.personalisationFields.forEach(function (f) {
                st.units[i][f.key] = first[f.key];
              });
            }
          }
          updateSummary();
        } else if (btn.hasAttribute('data-paste-toggle')) {
          st.pasteOpen = !st.pasteOpen;
          updateSummary();
        } else if (btn.hasAttribute('data-paste-apply')) {
          var area = nameLinesEl.querySelector('[data-paste-area][data-variant-id="' + variantId + '"]');
          if (area) {
            var firstRequired = config.personalisationFields.filter(function (f) { return f.required; })[0]
              || config.personalisationFields[0];
            var names = area.value.split(/\r?\n/).map(function (s) { return s.trim(); }).filter(Boolean);
            names.slice(0, st.units.length).forEach(function (name, i) {
              st.units[i][firstRequired.key] = name;
            });
            st.pasteOpen = false;
            updateSummary();
          }
        }
      });
    }

    // ---- Validation ----

    function validate(cells) {
      if (config.minTotalQty > 0 && getTotalQuantity(cells) < config.minTotalQty) {
        setStatus('Minimum order is ' + config.minTotalQty + ' units for this product.', 'error');
        return false;
      }

      if (!config.enableAddName) return true;

      // Flag units where optional fields are filled but a required field is blank.
      var firstBad = null;
      cells.forEach(function (cell) {
        var st = persState[cell.variantId];
        if (!st || !st.enabled) return;
        st.units.forEach(function (unit, idx) {
          if (!isPartialUnit(unit)) return;
          config.personalisationFields.forEach(function (f) {
            if (!f.required || (unit[f.key] || '').trim() !== '') return;
            var input = nameLinesEl.querySelector(
              '[data-name-input][data-variant-id="' + cell.variantId + '"][data-unit-index="' + idx + '"][data-field-key="' + f.key + '"]'
            );
            if (input) {
              input.classList.add('error');
              if (!firstBad) firstBad = input;
            }
          });
        });
      });
      if (firstBad) {
        setStatus('Some items have details entered but no name. Add the name, or clear the other fields to skip personalisation for that item.', 'error');
        firstBad.focus();
        return false;
      }
      return true;
    }

    // ---- Payload build + cart add ----

    function buildPayload(cells) {
      var items = [];
      var named = { total: 0, lines: 0 };

      cells.forEach(function (cell) {
        var st = persState[cell.variantId];
        var personalise = config.enableAddName && st && st.enabled;

        if (personalise) {
          var namedUnits = st.units.filter(isNamedUnit);
          namedUnits.forEach(function (unit) {
            var props = { _pid: uniquePid() };
            config.personalisationFields.forEach(function (f) {
              var val = (unit[f.key] || '').trim();
              if (val) props[f.key] = val;
            });
            items.push({ id: cell.variantId, quantity: 1, properties: props });
          });
          var unnamed = cell.quantity - namedUnits.length;
          if (unnamed > 0) {
            items.push({ id: cell.variantId, quantity: unnamed });
          }
          named.total += namedUnits.length;
          if (namedUnits.length > 0) named.lines += 1;
        } else {
          items.push({ id: cell.variantId, quantity: cell.quantity });
        }
      });

      var feeQty = feeQuantity(named);
      if (feeQty > 0 && feeVariant) {
        items.push({
          id: feeVariant.id,
          quantity: feeQty,
          properties: { _fee_for: config.addNameLabel || 'Add Name' }
        });
      }

      return { items: items, namedTotal: named.total, feeQty: feeQty };
    }

    var submitting = false;

    function addToCart() {
      if (submitting) return;
      var cells = getFilledCells();
      if (cells.length === 0) return;
      if (!validate(cells)) return;

      var payload = buildPayload(cells);
      var totalUnits = getTotalQuantity(cells);

      submitting = true;
      addBtn.disabled = true;
      setStatus('Adding to cart…', '');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: payload.items })
      })
        .then(function (response) {
          if (!response.ok) {
            return response.json().then(function (err) { throw err; });
          }
          return response.json();
        })
        .then(function () {
          var msg = totalUnits + ' item' + (totalUnits === 1 ? '' : 's') + ' added to cart';
          if (payload.namedTotal > 0) msg += ' (' + payload.namedTotal + ' personalised)';

          // Reset the form, then show the success message (updateSummary clears status).
          qtyInputs.forEach(function (input) {
            input.value = '';
            input.classList.remove('has-value');
          });
          persState = {};
          updateSummary(true);
          renderPersonalisation();
          setStatus(msg + '.', 'success');

          // Refresh the CartJS/Rivets mini-cart and header count, then open the drawer.
          if (typeof CartJS !== 'undefined') CartJS.getCart();
          fetch('/cart.js')
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              document.querySelectorAll('#CartCount, .cart-count, .header-cart-count').forEach(function (el) {
                el.textContent = cart.item_count;
              });
            })
            .catch(function () { /* count refresh is best-effort */ });
          setTimeout(function () {
            var headerCart = document.querySelector('.header-cart');
            if (headerCart) headerCart.click();
          }, 600);

          submitting = false;
        })
        .catch(function (err) {
          var msg = (err && (err.description || err.message)) || 'Error adding to cart. Please try again.';
          if (/sold out|inventory|stock/i.test(msg)) msg = msg + ' ' + SOLD_OUT_MSG;
          setStatus(msg, 'error');
          submitting = false;
          addBtn.disabled = false;
        });
    }

    addBtn.addEventListener('click', addToCart);

    // ---- Image thumbnail switching ----
    var featuredImg = container.querySelector('.b2b-matrix__featured-img');
    container.querySelectorAll('.b2b-matrix__thumb').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        container.querySelectorAll('.b2b-matrix__thumb').forEach(function (t) { t.classList.remove('active'); });
        thumb.classList.add('active');
        if (featuredImg) featuredImg.src = thumb.getAttribute('data-full-src');
      });
    });

    updateSummary();
  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-section-type="b2b-matrix"]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme editor support.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
